import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ModalService } from '@app/core/services/modal.service';
import { RememberService } from '@app/core/services/remember.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { SrReportMenuGet, SrReportService } from '@app/services/sr-report.service';

@Component({ selector: 'tt-sr-report-menu', templateUrl: './sr-report-menu.component.html', styleUrls: ['./sr-report-menu.component.css'], standalone: false })
export class SrReportMenuComponent implements OnInit, OnDestroy {
    public page?: SrReportMenuGet;
    public items: Item[] = [];
    public responsibleLabours: Item[] = [];
    public updatingStatus = false;
    public error = '';
    private initialized = false;
    private initTimer?: ReturnType<typeof setTimeout>;
    private destroyed = false;
    constructor(private readonly report: SrReportService, private readonly remember: RememberService, private readonly modal: ModalService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator) {
        this.navigator = useNavigatorFor(navigator, this);
    }
    public async ngOnInit(): Promise<void> {
        try { await this.loadPage(); } catch (error) { this.showError(error); }
    }
    private async loadPage(): Promise<void> {
        const previous = this.page;
        this.page = await this.report.getMenu({ checklistdata_keyno: String(this.navigator?.params['checklistdata_keyno'] ?? '') });
        clearTimeout(this.initTimer);
        this.initTimer = setTimeout(() => this.initialized = true, 50);
        // Reloaded server values also passed through the legacy watchers.
        this.rememberView(this.page.bg_srreportmenu_main, previous?.bg_srreportmenu_main);
        await Promise.all([this.loadItems(), this.loadResponsibleLabours(), this.saveResponsible(this.page.responsible_labour_no, previous?.responsible_labour_no)]);
    }
    private async loadItems(): Promise<void> {
        if (!this.page?.checklistdata_keyno?.trim()) return;
        this.items = [];
        this.items = await this.report.getMenuItems({ checklistdata_keyno: this.page.checklistdata_keyno });
    }
    private async loadResponsibleLabours(): Promise<void> {
        if (!this.page?.resource_id?.trim()) return;
        this.responsibleLabours = [];
        this.responsibleLabours = await this.report.getResponsibleLabours({ resource_id: this.page.resource_id });
    }
    public onViewChanged(value: string): void {
        if (!this.page || this.destroyed) return;
        const previous = this.page.bg_srreportmenu_main;
        this.page.bg_srreportmenu_main = value;
        this.rememberView(value, previous);
    }
    private rememberView(value: string, previous?: string): void {
        // Explicit remembering retains the legacy 50 ms initialization guard and variable name on the test route.
        if (!this.destroyed && this.initialized && previous !== undefined && value !== previous && value?.trim()) {
            void this.remember.remember('w_srreportmenu.bg_srreportmenu_main', value).catch(error => this.showError(error));
        }
    }
    public async onResponsibleChanged(value: string): Promise<void> {
        if (!this.page || this.destroyed) return;
        const previous = this.page.responsible_labour_no;
        this.page.responsible_labour_no = value;
        await this.saveResponsible(value, previous);
    }
    private async saveResponsible(value: string, previous?: string): Promise<void> {
        if (this.destroyed || previous === undefined || value === undefined || value === previous || !value?.trim() || !this.page?.checklistdata_keyno?.trim()) return;
        try {
            const response = await this.report.setResponsibleLabour({ checklistdata_keyno: this.page.checklistdata_keyno, responsible_labour_no: value });
            if (response.errorcode !== '0') await this.showWarning(response.errormessage);
        } catch (error) { this.showError(error); }
    }
    public async updateStatus(): Promise<void> {
        if (!this.page || this.updatingStatus) return;
        this.updatingStatus = true;
        try {
            const response = await this.report.changeStatus({ checklistdata_keyno: this.page.checklistdata_keyno });
            if (response.errorcode !== '0') await this.showWarning(response.errormessage);
            else { this.updatingStatus = false; await this.loadPage(); }
        } catch (error) { this.showError(error); }
        finally { this.updatingStatus = false; }
    }
    public openItem({ item, event }: { item: Item; event: MouseEvent }): void {
        if (item.item_state === 'customaction') {
            const action = item['item_action'];
            if (typeof action === 'function') action(item);
            return;
        }
        if (!item.item_state || item.item_path === '') return;
        const to = ['srtravelbill', 'srtravelbillspec', 'srreportmenu', 'srrefundbasis', 'srequipments', 'srrefundinputcrew'].includes(item.item_state) ? `${item.item_state}-test` : item.item_state;
        const target = { to, parms: item.item_parms };
        if (event.ctrlKey) this.navigator?.newTab(target); else this.navigator?.go(target);
    }
    private showWarning(message: string): Promise<void> {
        return this.modal.show({ type: 'warning', title: 'Varsel', message, buttons: [{ label: 'OK', cssClass: 'btn-warning', close: true }] });
    }
    private showError(error: unknown): void { this.error = error instanceof Error ? error.message : String(error); }
    public ngOnDestroy(): void { this.destroyed = true; clearTimeout(this.initTimer); }
}
